import { createFileRoute } from '@tanstack/react-router'

import { CHART_HEAD, CHART_UNDER_HEAD } from '@/components/charts/chart-header-layout'
import ScreenLayout from '@/components/layout/ScreenLayout'
import CardState from '@/components/ui/CardState'
import DataTableSkeleton from '@/components/ui/DataTableSkeleton'
import { SECTION_CARD } from '@/components/ui/section-card-variants'
import Skeleton from '@/components/ui/Skeleton'
import StatStripSkeleton from '@/components/ui/StatStripSkeleton'
import { failureMessage, portfolioFailure } from '@/features/portfolio/api/errors'
import {
  healthQueryOptions,
  useAnalytics,
  usePortfolioOverview,
  useProfitLoss,
  useShareholders,
  valueHistoryQueryOptions,
} from '@/features/portfolio/api/queries'
import { usePrefetch } from '@/hooks/usePrefetch'
import { cn } from '@/lib/utils'
import OverviewBody from '@/screens/portfolio/components/overview/OverviewBody'
import OverviewEmpty from '@/screens/portfolio/components/overview/OverviewEmpty'
import OverviewNoProfile from '@/screens/portfolio/components/overview/OverviewNoProfile'
import { VALUE_HISTORY_INITIAL_PERIOD } from '@/screens/portfolio/components/overview/ValueHistoryPanel'
import PortfolioFamilyHeader from '@/screens/portfolio/components/PortfolioFamilyHeader'
import { useActiveShareholderId } from '@/screens/portfolio/components/portfolio-switcher/activeProfile'

export const Route = createFileRoute('/_app/portfolio')({ component: OverviewScreen })

/** Sized against the loaded body's own shape, per `CardState`'s own contract:
 * a skeleton shorter than what it stands in for makes the page jump when the
 * real content lands. The value card (its chart head, curve and stat strip),
 * then the five-row holdings preview under its head. */
const OVERVIEW_SKELETON = (
  <div className="flex flex-col gap-6">
    <div className={cn(SECTION_CARD, 'flex flex-col gap-4')}>
      {/* The value card's one `ChartHeader`: "Market value" over the figure and its eye, the
          window's change under them, the periods right; the curve 24 under it. */}
      <div className="flex flex-col">
        <div className={CHART_HEAD.root}>
          <div className={CHART_HEAD.grid}>
            <div className={CHART_HEAD.info}>
              <div className="flex flex-col gap-1">
                <div className="flex h-4 items-center">
                  <Skeleton className="h-3 w-28" />
                </div>
                <div className="flex h-9 items-center gap-2">
                  <Skeleton className="h-8 w-56" />
                  <Skeleton className="size-9 rounded-full" />
                </div>
              </div>
              <Skeleton className="h-5 w-48" />
            </div>
            <div className={CHART_HEAD.actions}>
              <div className={CHART_HEAD.bare}>
                <Skeleton className="h-9 w-62" />
              </div>
            </div>
          </div>
        </div>
        <Skeleton className={cn(CHART_UNDER_HEAD, 'h-44 w-full rounded-md')} />
      </div>
      <StatStripSkeleton cells={5} surface="ruled" size="xl" sub />
    </div>
    <div className="flex flex-col gap-3">
      <div className="flex min-h-9 items-center">
        <Skeleton className="h-4 w-20" />
      </div>
      <DataTableSkeleton rows={5} columns={6} rowHeight="lg" />
    </div>
  </div>
)

/**
 * Portfolio · Overview (Figma `4236:70992`) — the value card and the holdings
 * preview in the main column. See
 * `src/screens/portfolio/components/overview/OverviewBody.tsx` for the
 * assembled body. The right rail (What's happening → Reminders → Portfolio
 * health) is the section's own `PortfolioRail`, which `ScreenLayout` draws on
 * every Portfolio page by route — the no-profile and empty pages included.
 *
 * `GET /overview/` is the ONE call this screen gates on: it returns the
 * summary AND the holdings together (contract §8.2), so the header total and
 * the rows beneath it can never disagree the way two separate reads could.
 * `GET /profit-loss/` is fetched alongside it but is NOT part of this gate —
 * `StatRow` (inside `OverviewBody`) carries its own independent
 * loading/error state for the two cells that need it: a slower second
 * endpoint should not hold up a row that is otherwise ready.
 *
 * ── Three shapes an HTTP-200 `/overview/` can mean, not two ─────────────────
 *
 * A member with no profile, or a profile with no positions, both get HTTP 200
 * and an empty `holdings` array — that is an ANSWER, not a failure. But they
 * are different pages in V2 (`portfolio.md` §3 vs §4), and `/overview/` alone
 * cannot tell them apart, so `useShareholders()` (already warm — the header
 * reads the same query) is what actually decides which one renders:
 *
 *   0 profiles                 `OverviewNoProfile` — no selector row; nothing
 *                               to switch to or manage yet.
 *   ≥1 profile, 0 holdings     `OverviewEmpty` — the switcher and ⋮ stay, the
 *                               add pill hides (`showAddAction={false}`).
 *   ≥1 profile, ≥1 holding     the real body, below.
 *
 * Both empty shapes wait on `shareholders` as well as `overview` before
 * deciding, so a first, cold load never flashes "no portfolio yet" and then
 * corrects itself to "no profile" a frame later.
 */
function OverviewScreen() {
  const { data: shareholders, isPending: shareholdersPending } = useShareholders()
  const shareholderId = useActiveShareholderId()
  const overviewQuery = usePortfolioOverview(shareholderId)
  const profitLossQuery = useProfitLoss(shareholderId)
  // Total return is the server's own (owner P4): `/analytics/`'s breakdown, dividends included.
  // Not a gate either — `StatRow` gives its cell its own pending / failed state.
  const analyticsQuery = useAnalytics(shareholderId)
  // `OverviewBody` reads `/health/` and `ValueHistoryPanel` reads
  // `/value-history/`, each with its own gate — but both mount only once
  // `/overview/` lands, so their requests used to leave a round-trip late.
  // They need nothing but the profile, so they start here, alongside the
  // gating read, on the keys those two readers use.
  usePrefetch(healthQueryOptions(shareholderId))
  usePrefetch(valueHistoryQueryOptions(VALUE_HISTORY_INITIAL_PERIOD, shareholderId))

  const failure = portfolioFailure(overviewQuery.error)

  const cardState = overviewQuery.isError
    ? 'failed'
    : overviewQuery.isPending || shareholdersPending
      ? 'loading'
      : 'loaded'

  if (cardState === 'loaded' && overviewQuery.data !== undefined) {
    // No profile (`5020:96537`) — its own bare title, no `PortfolioFamilyHeader`
    // at all: there is no book to switch between or act on yet.
    if ((shareholders?.length ?? 0) === 0) {
      return (
        // The mock's visible "No profile" title (`pfa-np__title`, 20/600) over the card: with no
        // book there is no selector row to name the page, so the title shows at every width.
        <ScreenLayout header={<h1 className="text-h-l">No profile</h1>} pageTitle="Portfolio">
          <OverviewNoProfile />
        </ScreenLayout>
      )
    }

    // No portfolio yet (`4236:75759`) — the switcher/⋮ stay, the add pill hides.
    if (overviewQuery.data.holdings.length === 0) {
      return (
        <ScreenLayout
          header={<PortfolioFamilyHeader tab="overview" showAddAction={false} />}
          pageTitle="Portfolio"
        >
          <OverviewEmpty />
        </ScreenLayout>
      )
    }
  }

  return (
    <ScreenLayout header={<PortfolioFamilyHeader tab="overview" />} pageTitle="Portfolio">
      <CardState
        state={cardState}
        onRetry={() => {
          void overviewQuery.refetch()
        }}
        failedTitle="Your book did not load"
        failedDescription={
          failure ? failureMessage(failure) : 'Something went wrong. Please try again.'
        }
        skeleton={OVERVIEW_SKELETON}
      >
        {overviewQuery.data ? (
          <OverviewBody
            overview={overviewQuery.data}
            profitLoss={profitLossQuery.data}
            profitLossPending={profitLossQuery.isPending}
            profitLossError={profitLossQuery.isError}
            returns={{
              data: analyticsQuery.data?.breakdown,
              pending: analyticsQuery.isPending,
              error: analyticsQuery.isError,
            }}
          />
        ) : null}
      </CardState>
    </ScreenLayout>
  )
}
